import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  CircularProgress,
  Container,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import { supabase } from '../lib/supabase';
import { TABLES, getCurrentSchoolYear, getSchoolYears, resolveSchoolYear } from '../lib/tables';
import SchoolYearSelect from '../components/SchoolYearSelect';
import SchoolYearBadge from '../components/SchoolYearBadge';
import {
  buildDataset,
  buildProfiles,
  buildRetrospective,
  forecastEndOfYear,
  itemBasedScores,
  kMeans,
  buildFeatureVectors,
  normalizeActivityKey,
  recommend,
  schoolMonthIndex,
  topNeighbors,
  totalRolloverCredits,
  userBasedScores,
} from '../lib/intelligence';
import { getPreviousSchoolYear } from '../lib/statistics';

const JsonBlock = ({ title, value }) => (
  <Box sx={{ mb: 3 }}>
    <Typography variant="subtitle1" fontWeight="bold" gutterBottom>{title}</Typography>
    <Box
      component="pre"
      sx={{ m: 0, p: 2, bgcolor: '#f8fafc', border: '1px solid #000000', borderRadius: 2, fontSize: 12, overflow: 'auto' }}
    >
      {JSON.stringify(value, null, 2)}
    </Box>
  </Box>
);

export default function IntelligenceDebug() {
  const [raw, setRaw] = useState(null);
  const [yearOptions, setYearOptions] = useState([]);
  const [selectedYear, setSelectedYear] = useState(null);
  const [loading, setLoading] = useState(true);
  const [report, setReport] = useState(null);
  const [cfStudentId, setCfStudentId] = useState(null);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      const [studentsRes, requestsRes, categoriesRes, activitiesRes, modulesRes, typesRes, calendarRes, years] =
        await Promise.all([
          supabase.from(TABLES.profiles).select('id, nume, prenume, clasa, specializare').eq('rol', 'elev'),
          supabase
            .from(TABLES.credit_requests)
            .select('id, id_elev, id_tip_credit, id_module, id_activitate_predefinita, nume_activitate, credite_solicitate, credite_acordate, status, data_cerere, data_validare, an_scolar')
            .eq('status', 'validat'),
          supabase.from(TABLES.credits_by_category).select('id_elev, an_scolar, credite_curriculare, credite_extracurriculare, credite_transferabile, credite_totale'),
          supabase
            .from(TABLES.predefined_activities)
            .select('id, id_tip_credit, id_module, nume_activitate, descriere, ore_minime, ore_recomandate, credite_per_ora, credite_acordate, este_activ')
            .eq('este_activ', true),
          supabase.from(TABLES.modules).select('id, cod_module, nume_module, credite_tinta, ordine').order('ordine'),
          supabase.from(TABLES.credit_types).select('id, nume_tip, categorie'),
          supabase.from('academic_calendar_v2').select('an_scolar, id_module, data_start, data_end'),
          getSchoolYears(TABLES.credit_requests),
        ]);
      if (!mounted) return;
      setRaw({
        students: studentsRes.data || [],
        requests: requestsRes.data || [],
        categories: categoriesRes.data || [],
        activities: activitiesRes.data || [],
        modules: modulesRes.data || [],
        creditTypes: typesRes.data || [],
        calendar: calendarRes.data || [],
      });
      setYearOptions(years);
      setSelectedYear((previous) => previous ?? resolveSchoolYear(years));
      setLoading(false);
    };
    load();
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    if (!raw || !selectedYear || selectedYear === 'all') return;
    const dataset = buildDataset(raw);
    const profiles = buildProfiles(dataset, selectedYear);
    const previousYear = getPreviousSchoolYear(selectedYear);
    const previousProfiles = previousYear ? buildProfiles(dataset, previousYear) : new Map();

    const cluster = kMeans(buildFeatureVectors(profiles), 3);
    const monthIndex = Math.min(9, Math.max(0, schoolMonthIndex(new Date().toISOString())));

    const profileList = [...profiles.values()];
    const activeProfiles = profileList.filter((profile) => profile.totalCredits > 0);

    const forecastSample = (activeProfiles.length ? activeProfiles : profileList).slice(0, 5).map((profile) => {
      const forecast = forecastEndOfYear(profile.monthlyCumulative, dataset.targetSum, monthIndex);
      return {
        student: `${profile.nume} ${profile.prenume}`,
        totalCredits: profile.totalCredits,
        activityCredits: profile.activityCredits,
        rolloverCredits: profile.rolloverCredits,
        creditsByModule: profile.creditsByModule,
        creditsByCategory: profile.creditsByCategory,
        activityCount: profile.activityCount,
        diversityScore: Number(profile.diversityScore.toFixed(3)),
        percentileInClass: Number(profile.percentileInClass.toFixed(1)),
        estimated: Math.round(forecast.estimated),
        probability: Number(forecast.probability.toFixed(3)),
      };
    });

    const normalizeText = (value) =>
      String(value || '')
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '');
    const hasTestActivity = (profile) =>
      (profile.activityNames || []).some((name) => String(name).startsWith('TEST_'));

    const cfOptions = profileList
      .filter((profile) => profile.activityCount > 0)
      .map((profile) => ({
        id: profile.studentId,
        label: `${profile.nume} ${profile.prenume}`,
        hasTest: hasTestActivity(profile),
      }));

    const activitySample =
      profileList.find((profile) => profile.studentId === cfStudentId) ||
      profileList.find(hasTestActivity) ||
      profileList.find((profile) => profile.activityCount > 0) ||
      profileList.find((profile) => normalizeText(`${profile.nume} ${profile.prenume}`).includes('surca')) ||
      null;
    const cfUser = activitySample
      ? [...userBasedScores(dataset, profiles, activitySample.studentId, 5).values()]
          .sort((a, b) => b.score - a.score)
          .slice(0, 5)
      : [];
    const cfItem = activitySample
      ? [...itemBasedScores(dataset, profiles, activitySample.studentId).values()]
          .sort((a, b) => b.score - a.score)
          .slice(0, 5)
      : [];
    const neighbors = activitySample ? topNeighbors(profiles, activitySample.studentId, 5) : [];

    const sampleStudents = [
      activitySample,
      ...profileList.filter((profile) => profile.studentId !== activitySample?.studentId),
    ]
      .filter(Boolean)
      .slice(0, 3);

    const keysByStudent = new Map();
    dataset.requests
      .filter((request) => request.an_scolar === selectedYear)
      .forEach((request) => {
        if (!request.id_activitate_predefinita && !request.nume_activitate) return;
        const key = normalizeActivityKey(
          request.id_activitate_predefinita
            ? `id:${request.id_activitate_predefinita}`
            : `name:${request.nume_activitate}`,
        );
        const set = keysByStudent.get(request.id_elev) || new Set();
        set.add(key);
        keysByStudent.set(request.id_elev, set);
      });

    const entries = [...keysByStudent.entries()];
    const overlapMatrix = { pairs1plus: 0, pairs2plus: 0, pairs3plus: 0 };
    for (let i = 0; i < entries.length; i += 1) {
      for (let j = i + 1; j < entries.length; j += 1) {
        let shared = 0;
        entries[i][1].forEach((key) => {
          if (entries[j][1].has(key)) shared += 1;
        });
        if (shared >= 1) overlapMatrix.pairs1plus += 1;
        if (shared >= 2) overlapMatrix.pairs2plus += 1;
        if (shared >= 3) overlapMatrix.pairs3plus += 1;
      }
    }

    const recommendationSample = sampleStudents.map((profile) => {
      const suggestions = recommend(dataset, profiles, profile.studentId, { k: 5 });
      return {
        student: `${profile.nume} ${profile.prenume}`,
        suggestions: suggestions.map((item) => ({
          activity: item.activityName,
          module: item.module,
          modules: item.modules,
          moduleLabel: item.moduleLabel,
          credits: item.estimatedCredits,
          finalScore: item.finalScore,
          breakdown: item.breakdown,
          explanations: item.explanation,
        })),
        modules: suggestions.map((item) => item.module),
        moduleDiversity: new Set(suggestions.map((item) => item.module)).size,
      };
    });

    const currentYear = getCurrentSchoolYear();
    const retrospectiveProfiles = selectedYear === currentYear ? previousProfiles : profiles;
    const retrospectiveSample = [...retrospectiveProfiles.values()]
      .filter((profile) => profile.activityCount > 0)
      .sort((a, b) => b.totalCredits - a.totalCredits)
      .slice(0, 3)
      .map((profile) => buildRetrospective(dataset, retrospectiveProfiles, profile.studentId))
      .filter(Boolean);

    setReport({
      schoolYear: selectedYear,
      students: dataset.students.length,
      validatedRequests: dataset.requests.filter((request) => request.an_scolar === selectedYear).length,
      rolloverCredits: totalRolloverCredits(dataset, selectedYear),
      targetSum: dataset.targetSum,
      clusterSizes: cluster.members.map((members, index) => ({ cluster: index, size: members.length })),
      clusterIterations: cluster.iterations,
      clusterInertia: Number(cluster.inertia.toFixed(2)),
      activitySample: activitySample
        ? {
            student: `${activitySample.nume} ${activitySample.prenume}`,
            activityCount: activitySample.activityCount,
            activityKeys: activitySample.activityKeys.slice(0, 5),
            activityVector: activitySample.activityVector.slice(0, 5),
            activityKeyIndex: Object.fromEntries([...activitySample.activityKeyIndex].slice(0, 5)),
            activityNames: activitySample.activityNames.slice(0, 5),
          }
        : null,
      cfUser,
      cfItem,
      neighbors,
      cfOptions,
      cfStudentId: activitySample?.studentId || null,
      cfMessage:
        activitySample && cfUser.length === 0 && cfItem.length === 0
          ? 'Elev cu profil izolat (doar importuri). CF nu are candidate.'
          : null,
      overlapMatrix,
      forecasts: forecastSample,
      recommendations: recommendationSample,
      retrospectives: retrospectiveSample,
    });
  }, [raw, selectedYear, cfStudentId]);

  if (loading || !report) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="40vh">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, flexWrap: 'wrap', mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight="bold">Diagnostic sistem inteligent</Typography>
          <Typography variant="body2" color="text.secondary">
            Output real al modulelor din src/lib/intelligence (pagina tehnica, fara meniu).
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <SchoolYearSelect value={selectedYear} options={yearOptions} onChange={setSelectedYear} />
          <SchoolYearBadge year={selectedYear} />
        </Box>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', mb: 2 }}>
        <FormControl size="small" sx={{ minWidth: 280 }}>
          <InputLabel>Elev pentru CF</InputLabel>
          <Select
            label="Elev pentru CF"
            value={report.cfStudentId || ''}
            onChange={(event) => setCfStudentId(event.target.value)}
          >
            {report.cfOptions.map((option) => (
              <MenuItem key={option.id} value={option.id}>
                {option.label}{option.hasTest ? ' (TEST_)' : ''}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {report.cfMessage && <Alert severity="info">{report.cfMessage}</Alert>}
      </Box>

      <JsonBlock title="Sumar dataset" value={{
        schoolYear: report.schoolYear,
        students: report.students,
        validatedRequests: report.validatedRequests,
        rolloverCredits: report.rolloverCredits,
        targetSum: report.targetSum,
      }} />
      <JsonBlock title="Profiluri si predictii (esantion)" value={report.forecasts} />
      <JsonBlock title="Activity vector (esantion)" value={report.activitySample} />
      <JsonBlock title="CF scores (user-based / item-based)" value={{ userCF: report.cfUser, itemCF: report.cfItem }} />
      <JsonBlock title="Vecini CF (top 5, cosine)" value={report.neighbors} />
      <JsonBlock title="Overlap matrix (perechi de elevi cu activitati comune)" value={report.overlapMatrix} />
      <JsonBlock title="Clustering k-means (k=3)" value={{
        clusterSizes: report.clusterSizes,
        iterations: report.clusterIterations,
        inertia: report.clusterInertia,
      }} />
      <JsonBlock title="Recomandari (esantion, top 5 per elev)" value={report.recommendations} />
      <JsonBlock title="Retrospectiv an incheiat (esantion)" value={report.retrospectives} />
    </Container>
  );
}
